import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { FaChevronDown, FaTimes } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import * as XLSX from "xlsx";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchCaseLifecycleHistory,
  fetchCaseLifecycleReport,
  type CaseLifecycleRow,
} from "../../store/superAdmin/dataExtractSlice";
import { fetchAllEmployees } from "../../store/allocation/employeeSlice";
import {
  AdminHeaderButton,
  AdminPageHeader,
  AdminPageShell,
  AdminSearchInput,
  AdminTableCard,
  AdminTableState,
  formatAdminDateTime,
} from "../../components/admin/AdminPageShell";
import { CustomDatePicker } from "../../components/common/DatePickers";
import { caseHrefFromAllocationId } from "../../utils/caseIds";
import { canAccessCaseLifecycleReport } from "../../utils/employeeRoles";
import {
  Qc2CreditLabel,
  qc2CreditText,
  WorkCreditLabel,
  workCreditText,
  workCreditTotal,
} from "../../utils/workCreditDisplay";
import { matchesRedesignBucket, type RedesignBucketFilter } from "../../utils/redesignBuckets";
import { RedesignBucketToggle } from "../../components/common/RedesignBucketToggle";

type CaseKindFilter =
  | "all"
  | "live"
  | "redesign"
  | "external"
  | "internal";

type ReferenceFilter = "all" | "old_logs";

type ColumnFilters = {
  caseKind: CaseKindFilter;
  allocatedBy: string;
  qc1By: string;
  designedBy: string;
  qc2By: string;
  uploadBy: string;
  reference: ReferenceFilter;
};

const DEFAULT_FILTERS: ColumnFilters = {
  caseKind: "all",
  allocatedBy: "",
  qc1By: "",
  designedBy: "",
  qc2By: "",
  uploadBy: "",
  reference: "all",
};

const CASE_KIND_OPTIONS: { value: CaseKindFilter; label: string }[] = [
  { value: "all", label: "All Cases" },
  { value: "live", label: "Live" },
  { value: "redesign", label: "Redesign (external+internal)" },
  { value: "external", label: "External" },
  { value: "internal", label: "Internal" },
];

const REFERENCE_OPTIONS: { value: ReferenceFilter; label: string }[] = [
  { value: "all", label: "All cases" },
  { value: "old_logs", label: "Only old log cases" },
];

const STAFF_ROLES = new Set([
  "admin",
  "super admin",
  "captain",
  "designer",
  "senior designer",
  "assistant captain",
]);

const UPLOAD_ROLES = new Set(["crm", "super admin"]);

const localIsoDate = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const daysBeforeToday = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return localIsoDate(d);
};

const kindLabel = (kind: CaseLifecycleRow["kind"]) => {
  if (kind === "redesign") return "External";
  if (kind === "internal_redesign") return "Internal";
  return "Live";
};

const splitNames = (value?: string | null) =>
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

const nameMatches = (haystack: string | null | undefined, needle: string) => {
  if (!needle) return true;
  const parts = String(haystack || "")
    .split(",")
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  const n = needle.trim().toLowerCase();
  return parts.some((p) => p === n || p.includes(n));
};

const matchesCaseKind = (kind: CaseLifecycleRow["kind"], filter: CaseKindFilter) => {
  if (filter === "all") return true;
  if (filter === "live") return kind === "live";
  if (filter === "external") return kind === "redesign";
  if (filter === "internal") return kind === "internal_redesign";
  if (filter === "redesign") {
    return kind === "redesign" || kind === "internal_redesign";
  }
  return true;
};

const uniqueSorted = (values: string[]) =>
  Array.from(new Set(values.map((v) => v.trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b),
  );

const exportLifecycleExcel = (
  rows: CaseLifecycleRow[],
  opts: {
    date: string;
    filterLabel: string;
    totalDelUnits: number;
    totalDesignedUnits: number;
    totalQc2Units: number;
  },
) => {
  const excelRows: Array<Record<string, string | number>> = rows.map((row) => ({
    "Case ID": row.case_id,
    Type: kindLabel(row.kind),
    "Created At": row.created_at ? formatAdminDateTime(row.created_at) : "",
    Category: row.kind === "live" ? "" : row.category || "",
    "Del Units": row.delivery_units ?? "",
    "Del Units Marked": row.delivery_units_marked ? "Yes" : "No",
    "Allocated By": row.allocated_by || "",
    "Allocated At": row.allocated_at ? formatAdminDateTime(row.allocated_at) : "",
    "QC-1": row.qc1_by || "",
    "QC-1 At": row.qc1_at ? formatAdminDateTime(row.qc1_at) : "",
    Designed: workCreditText(
      row.designed_by,
      row.design_unit,
      row.designer_units,
      row.model_by,
      row.model_units,
    ),
    "Designed At": row.designed_at ? formatAdminDateTime(row.designed_at) : "",
    "QC-2": qc2CreditText(row.qc2_by, row.design_unit, row.model_units),
    "QC-2 At": row.qc2_at ? formatAdminDateTime(row.qc2_at) : "",
    "Upload By": row.upload_by || "",
    "Upload At": row.upload_at ? formatAdminDateTime(row.upload_at) : "",
    "Live Case ID": row.live_case_id,
  }));

  excelRows.push({
    "Case ID": "TOTAL",
    Type: opts.filterLabel,
    "Created At": "",
    Category: "",
    "Del Units": opts.totalDelUnits,
    "Del Units Marked": "",
    "Allocated By": "",
    "Allocated At": "",
    "QC-1": "",
    "QC-1 At": "",
    Designed: opts.totalDesignedUnits,
    "Designed At": "",
    "QC-2": opts.totalQc2Units,
    "QC-2 At": "",
    "Upload By": "",
    "Upload At": "",
    "Live Case ID": `${rows.length} cases`,
  });

  const worksheet = XLSX.utils.json_to_sheet(excelRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Case Report");
  XLSX.writeFile(workbook, `Case_Lifecycle_${opts.date}.xlsx`);
};

const kindBadgeClass = (kind: CaseLifecycleRow["kind"]) => {
  if (kind === "redesign") return "bg-amber-50 text-amber-800";
  if (kind === "internal_redesign") return "bg-violet-50 text-violet-800";
  return "bg-[#E8F6F8] text-[#1A6B78]";
};

/** Generator normal; uploader green; next-IR creator pink. */
const UploadByLabel = ({
  value,
  tone,
}: {
  value?: string | null;
  tone: CaseLifecycleRow["upload_bg"];
}) => {
  const raw = value?.trim() || "";
  if (!raw) return <span>—</span>;

  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2 || !tone) {
    return <span className="text-[#2C2C2C]">{raw}</span>;
  }

  const secondClass =
    tone === "green"
      ? "text-emerald-600"
      : tone === "red"
        ? "text-pink-500"
        : "text-[#2C2C2C]";

  return (
    <span className="text-[#2C2C2C]">
      {parts[0]}
      <span className="text-[#8A8A85]">, </span>
      <span className={secondClass}>{parts.slice(1).join(", ")}</span>
    </span>
  );
};

const PersonTimeCell = ({
  name,
  at,
  className = "",
}: {
  name?: ReactNode;
  at?: string | null;
  className?: string;
}) => (
  <td className={`py-3 px-4 align-top text-sm text-[#4A4A48] ${className}`}>
    <div className="font-medium text-[#2C2C2C]">
      {name == null || name === "" ? "—" : name}
    </div>
    <div className="mt-0.5 text-[11px] text-[#8A8A85]">
      {at ? formatAdminDateTime(at) : "—"}
    </div>
  </td>
);

const ColumnFilter = ({
  label,
  value,
  options,
  onChange,
  active,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  active?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, query]);

  const selectedLabel =
    options.find((opt) => opt.value === value)?.label || label;

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex max-w-full items-center gap-1 rounded-md px-1 py-0.5 text-left text-[11px] font-semibold uppercase tracking-wider transition ${
          active
            ? "bg-[#E8F6F8] text-[#1A6B78]"
            : "text-[#8A8A85] hover:bg-[#F0F0EC]"
        }`}
        title={selectedLabel}
      >
        <span className="truncate">{label}</span>
        <FaChevronDown className="shrink-0 text-[9px] opacity-70" />
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-30 mt-1 w-[220px] rounded-xl border border-[#E8E8E4] bg-white p-2 shadow-lg">
          {options.length > 8 ? (
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="mb-2 w-full rounded-lg border border-[#E3E8E8] px-2 py-1.5 text-xs text-[#2C2C2C] outline-none focus:border-[#2fa9bf]"
            />
          ) : null}
          <div className="max-h-56 overflow-y-auto">
            {filteredOptions.map((opt) => (
              <button
                key={opt.value || "__all__"}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                  setQuery("");
                }}
                className={`block w-full rounded-lg px-2 py-1.5 text-left text-xs transition hover:bg-[#F5F5F2] ${
                  value === opt.value
                    ? "font-semibold text-[#2fa9bf]"
                    : "text-[#4A4A48]"
                }`}
              >
                {opt.label}
              </button>
            ))}
            {!filteredOptions.length ? (
              <div className="px-2 py-2 text-xs text-[#8A8A85]">No matches</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const LifecycleTable = ({
  rows,
  loading,
  error,
  emptyMessage,
  onOpenReference,
  referenceLoadingId,
  showReference = false,
  filters,
  onFiltersChange,
  staffNames,
  uploadNames,
}: {
  rows: CaseLifecycleRow[];
  loading?: boolean;
  error?: string | null;
  emptyMessage: string;
  onOpenReference?: (row: CaseLifecycleRow) => void;
  referenceLoadingId?: string | null;
  showReference?: boolean;
  filters?: ColumnFilters;
  onFiltersChange?: (next: ColumnFilters) => void;
  staffNames?: string[];
  uploadNames?: string[];
}) => {
  const colSpan = showReference ? 8 : 7;
  const canFilter = Boolean(filters && onFiltersChange);
  const staffOptions = useMemo(
    () => [
      { value: "", label: "All" },
      ...(staffNames || []).map((name) => ({ value: name, label: name })),
    ],
    [staffNames],
  );
  const uploadOptions = useMemo(
    () => [
      { value: "", label: "All" },
      ...(uploadNames || []).map((name) => ({ value: name, label: name })),
    ],
    [uploadNames],
  );

  return (
    <AdminTableCard>
      <table className="min-w-full text-left">
        <thead className="border-b border-[#E8E8E4] bg-[#FAFAF8]">
          <tr>
            <th className="py-3 px-4 align-bottom">
              {canFilter && filters && onFiltersChange ? (
                <ColumnFilter
                  label="Case ID"
                  value={filters.caseKind}
                  active={filters.caseKind !== "all"}
                  options={CASE_KIND_OPTIONS}
                  onChange={(value) =>
                    onFiltersChange({
                      ...filters,
                      caseKind: value as CaseKindFilter,
                    })
                  }
                />
              ) : (
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8A85]">
                  Case ID
                </span>
              )}
            </th>
            <th className="py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-[#8A8A85]">
              Del Units
            </th>
            <th className="py-3 px-4 align-bottom">
              {canFilter && filters && onFiltersChange ? (
                <ColumnFilter
                  label="Allocation"
                  value={filters.allocatedBy}
                  active={Boolean(filters.allocatedBy)}
                  options={staffOptions}
                  onChange={(value) =>
                    onFiltersChange({ ...filters, allocatedBy: value })
                  }
                />
              ) : (
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8A85]">
                  Allocation
                </span>
              )}
            </th>
            <th className="py-3 px-4 align-bottom">
              {canFilter && filters && onFiltersChange ? (
                <ColumnFilter
                  label="QC-1"
                  value={filters.qc1By}
                  active={Boolean(filters.qc1By)}
                  options={staffOptions}
                  onChange={(value) =>
                    onFiltersChange({ ...filters, qc1By: value })
                  }
                />
              ) : (
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8A85]">
                  QC-1
                </span>
              )}
            </th>
            <th className="py-3 px-4 align-bottom">
              {canFilter && filters && onFiltersChange ? (
                <ColumnFilter
                  label="Designed"
                  value={filters.designedBy}
                  active={Boolean(filters.designedBy)}
                  options={staffOptions}
                  onChange={(value) =>
                    onFiltersChange({ ...filters, designedBy: value })
                  }
                />
              ) : (
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8A85]">
                  Designed
                </span>
              )}
            </th>
            <th className="py-3 px-4 align-bottom">
              {canFilter && filters && onFiltersChange ? (
                <ColumnFilter
                  label="QC-2"
                  value={filters.qc2By}
                  active={Boolean(filters.qc2By)}
                  options={staffOptions}
                  onChange={(value) =>
                    onFiltersChange({ ...filters, qc2By: value })
                  }
                />
              ) : (
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8A85]">
                  QC-2
                </span>
              )}
            </th>
            <th className="py-3 px-4 align-bottom">
              {canFilter && filters && onFiltersChange ? (
                <ColumnFilter
                  label="Upload by"
                  value={filters.uploadBy}
                  active={Boolean(filters.uploadBy)}
                  options={uploadOptions}
                  onChange={(value) =>
                    onFiltersChange({ ...filters, uploadBy: value })
                  }
                />
              ) : (
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8A85]">
                  Upload by
                </span>
              )}
            </th>
            {showReference ? (
              <th className="py-3 px-4 align-bottom">
                {canFilter && filters && onFiltersChange ? (
                  <ColumnFilter
                    label="Reference"
                    value={filters.reference}
                    active={filters.reference !== "all"}
                    options={REFERENCE_OPTIONS}
                    onChange={(value) =>
                      onFiltersChange({
                        ...filters,
                        reference: value as ReferenceFilter,
                      })
                    }
                  />
                ) : (
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8A85]">
                    Reference
                  </span>
                )}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F0F0EC]">
          {loading ? (
            <AdminTableState
              colSpan={colSpan}
              variant="loading"
              message="Loading cases…"
            />
          ) : error ? (
            <AdminTableState colSpan={colSpan} variant="error" message={error} />
          ) : !rows.length ? (
            <AdminTableState
              colSpan={colSpan}
              variant="empty"
              message={emptyMessage}
            />
          ) : (
            rows.map((row) => {
              const refDisabled = !row.has_reference || !onOpenReference;
              const refBusy = referenceLoadingId === row.live_case_id;
              return (
                <tr
                  key={`${row.kind}-${row.case_id}`}
                  className="hover:bg-[#FBFBF9]"
                >
                  <td className="py-3 px-4 align-top">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <a
                        href={caseHrefFromAllocationId(row.case_id)}
                        className="font-semibold text-[#2fa9bf] hover:underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.case_id}
                      </a>
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${kindBadgeClass(row.kind)}`}
                      >
                        {kindLabel(row.kind)}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-[#8A8A85]">
                      {row.created_at ? formatAdminDateTime(row.created_at) : "—"}
                    </div>
                    {row.kind !== "live" && row.category ? (
                      <div className="mt-1 text-[11px] leading-snug text-[#6B6B66]">
                        {row.category}
                      </div>
                    ) : null}
                  </td>
                  <td
                    className={`py-3 px-4 align-top text-sm font-medium ${
                      row.delivery_units_marked
                        ? "text-[#2C2C2C]"
                        : "text-pink-500"
                    }`}
                    title={
                      row.delivery_units_marked
                        ? "Marked by unit allocator"
                        : "Not marked by unit allocator (system / designer / captain / unassigned)"
                    }
                  >
                    {row.delivery_units != null ? row.delivery_units : "—"}
                  </td>
                  <PersonTimeCell name={row.allocated_by} at={row.allocated_at} />
                  <PersonTimeCell name={row.qc1_by} at={row.qc1_at} />
                  <PersonTimeCell
                    name={
                      <WorkCreditLabel
                        designedBy={row.designed_by}
                        designUnit={row.design_unit}
                        designerUnits={row.designer_units}
                        modelBy={row.model_by}
                        modelUnits={row.model_units}
                      />
                    }
                    at={row.designed_at}
                  />
                  <PersonTimeCell
                    name={
                      <Qc2CreditLabel
                        name={row.qc2_by}
                        designUnit={row.design_unit}
                        modelUnits={row.model_units}
                      />
                    }
                    at={row.qc2_at}
                  />
                  <td className="py-3 px-4 align-top text-sm">
                    <div className="font-medium">
                      <UploadByLabel
                        value={row.upload_by}
                        tone={row.upload_bg}
                      />
                    </div>
                    <div className="mt-0.5 text-[11px] text-[#8A8A85]">
                      {row.upload_at ? formatAdminDateTime(row.upload_at) : "—"}
                    </div>
                  </td>
                  {showReference ? (
                    <td className="py-3 px-4 align-middle">
                      <button
                        type="button"
                        disabled={refDisabled || refBusy}
                        onClick={() => onOpenReference?.(row)}
                        title={
                          refDisabled
                            ? "No internal or external redesigns for this case"
                            : `View all allocations for ${row.live_case_id}`
                        }
                        className="rounded-lg border border-[#DDDDD8] bg-white px-2.5 py-1.5 text-xs font-medium text-[#4A4A48] transition enabled:hover:border-[#2fa9bf] enabled:hover:text-[#2fa9bf] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {refBusy ? "Loading…" : "Old logs"}
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </AdminTableCard>
  );
};

const CaseLifecycleReport = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { caseLifecycleReport, caseLifecycleHistory, loading, error } =
    useAppSelector((state) => state.dataExtract);
  const { allEmployees } = useAppSelector((state) => state.employee);

  const allowed = canAccessCaseLifecycleReport(user?.role, employee?.role);

  const [date, setDate] = useState(() => daysBeforeToday(2));
  const [employeeName, setEmployeeName] = useState("");
  const [reasonFilter, setReasonFilter] = useState<RedesignBucketFilter>("all");
  const [filters, setFilters] = useState<ColumnFilters>(DEFAULT_FILTERS);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLiveId, setModalLiveId] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    void dispatch(fetchCaseLifecycleReport({ date }));
  }, [dispatch, date, allowed]);

  useEffect(() => {
    if (!allowed) return;
    if (!allEmployees.length) {
      void dispatch(fetchAllEmployees());
    }
  }, [dispatch, allEmployees.length, allowed]);

  const rows = caseLifecycleReport?.rows || [];

  const staffNames = useMemo(() => {
    const fromRoster = allEmployees
      .filter((emp) => STAFF_ROLES.has(String(emp.role || "").trim().toLowerCase()))
      .map((emp) => String(emp.emp_name || "").trim());
    const fromRows = rows.flatMap((row) => [
      ...splitNames(row.allocated_by),
      ...splitNames(row.qc1_by),
      ...splitNames(row.designed_by),
      ...splitNames(row.model_by),
      ...splitNames(row.qc2_by),
    ]);
    return uniqueSorted([...fromRoster, ...fromRows]);
  }, [allEmployees, rows]);

  const employeeOptions = useMemo(() => {
    const fromRoster = allEmployees
      .filter((emp) => STAFF_ROLES.has(String(emp.role || "").trim().toLowerCase()))
      .map((emp) => String(emp.emp_name || "").trim());
    const fromRows = rows.flatMap((row) => [
      ...splitNames(row.designed_by),
      ...splitNames(row.model_by),
      ...splitNames(row.qc2_by),
      ...(row.filled_order_patients || []).map((patient) => patient.name),
    ]);
    return uniqueSorted([...fromRoster, ...fromRows]);
  }, [allEmployees, rows]);

  const uploadNames = useMemo(() => {
    const fromRoster = allEmployees
      .filter((emp) => UPLOAD_ROLES.has(String(emp.role || "").trim().toLowerCase()))
      .map((emp) => String(emp.emp_name || "").trim());
    const fromRows = rows.flatMap((row) =>
      String(row.upload_by || "")
        .split(",")
        .map((p) => p.trim()),
    );
    return uniqueSorted([...fromRoster, ...fromRows]);
  }, [allEmployees, rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (!matchesCaseKind(row.kind, filters.caseKind)) return false;
      if (employeeName) {
        const designed =
          nameMatches(row.designed_by, employeeName) ||
          nameMatches(row.model_by, employeeName);
        const qc2 = nameMatches(row.qc2_by, employeeName);
        const patient = (row.filled_order_patients || []).some((entry) =>
          nameMatches(entry.name, employeeName),
        );
        if (!designed && !qc2 && !patient) return false;
      }
      if (!nameMatches(row.allocated_by, filters.allocatedBy)) return false;
      if (!nameMatches(row.qc1_by, filters.qc1By)) return false;
      if (
        filters.designedBy &&
        !nameMatches(row.designed_by, filters.designedBy) &&
        !nameMatches(row.model_by, filters.designedBy)
      ) {
        return false;
      }
      if (!nameMatches(row.qc2_by, filters.qc2By)) return false;
      if (!nameMatches(row.upload_by, filters.uploadBy)) return false;
      if (filters.reference === "old_logs" && !row.has_reference) return false;
      if (!matchesRedesignBucket(reasonFilter, row.category, row.kind)) return false;
      if (!q) return true;
      return (
        row.case_id.toLowerCase().includes(q) ||
        row.live_case_id.toLowerCase().includes(q) ||
        (row.allocated_by || "").toLowerCase().includes(q) ||
        (row.qc1_by || "").toLowerCase().includes(q) ||
        (row.designed_by || "").toLowerCase().includes(q) ||
        (row.model_by || "").toLowerCase().includes(q) ||
        (row.qc2_by || "").toLowerCase().includes(q) ||
        (row.upload_by || "").toLowerCase().includes(q) ||
        (row.category || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, filters, reasonFilter, employeeName]);

  const totalDelUnits = useMemo(
    () =>
      filtered.reduce(
        (sum, row) => sum + (row.delivery_units != null ? row.delivery_units : 0),
        0,
      ),
    [filtered],
  );

  const totalDesignedUnits = useMemo(
    () =>
      filtered.reduce(
        (sum, row) =>
          sum +
          workCreditTotal(
            row.designed_by,
            row.design_unit,
            row.designer_units,
            row.model_by,
            row.model_units,
          ),
        0,
      ),
    [filtered],
  );

  const totalQc2Units = useMemo(
    () =>
      filtered.reduce((sum, row) => {
        if (!String(row.qc2_by || "").trim()) return sum;
        const design = row.design_unit != null ? row.design_unit : 0;
        const model = Object.values(row.model_units || {}).reduce(
          (units, value) => units + (Number(value) > 0 ? Number(value) : 0),
          0,
        );
        return sum + design + model;
      }, 0),
    [filtered],
  );

  const filtersActive =
    filters.caseKind !== "all" ||
    Boolean(filters.allocatedBy) ||
    Boolean(filters.qc1By) ||
    Boolean(filters.designedBy) ||
    Boolean(filters.qc2By) ||
    Boolean(filters.uploadBy) ||
    filters.reference !== "all" ||
    reasonFilter !== "all" ||
    Boolean(employeeName) ||
    Boolean(search.trim());

  const openReference = async (row: CaseLifecycleRow) => {
    if (!row.has_reference) return;
    setModalLiveId(row.live_case_id);
    setModalOpen(true);
    setHistoryError(null);
    setHistoryLoading(true);
    try {
      await dispatch(
        fetchCaseLifecycleHistory({ live_case_id: row.live_case_id }),
      ).unwrap();
    } catch (err: any) {
      setHistoryError(
        typeof err === "string" ? err : err?.message || "Failed to load history",
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalLiveId(null);
    setHistoryError(null);
  };

  const historyRows =
    caseLifecycleHistory?.live_case_id === modalLiveId
      ? caseLifecycleHistory.rows
      : [];

  const windowLabel = caseLifecycleReport?.window
    ? `${caseLifecycleReport.window.from} → ${caseLifecycleReport.window.to} IST`
    : "12:00 → next day 12:00 IST";

  const filterLabel =
    CASE_KIND_OPTIONS.find((o) => o.value === filters.caseKind)?.label ||
    "All Cases";

  if (!allowed) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  return (
    <AdminPageShell>
      <Helmet>
        <title>Case Lifecycle Report | Super Admin</title>
      </Helmet>

      <AdminPageHeader
        title="Case Lifecycle Report"
        subtitle={`Live + redesign cases created in the selected operational day (${windowLabel})`}
      >
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          <AdminSearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search case / person…"
            className="w-full sm:w-[240px]"
            size="sm"
          />
          <select
            value={employeeName}
            onChange={(event) => setEmployeeName(event.target.value)}
            className="h-9 w-full sm:w-[200px] rounded-lg border border-white/20 bg-white px-2 text-sm text-[#2C2C2C]"
            title="Cases this employee designed or QC-2’d"
          >
            <option value="">All employees</option>
            {employeeOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <CustomDatePicker value={date} onChange={setDate} variant="header" />
          <RedesignBucketToggle value={reasonFilter} onChange={setReasonFilter} />
          {filtersActive ? (
            <AdminHeaderButton
              type="button"
              onClick={() => {
                setFilters(DEFAULT_FILTERS);
                setReasonFilter("all");
                setEmployeeName("");
              }}
              title="Clear column filters"
            >
              Clear filters
            </AdminHeaderButton>
          ) : null}
          <AdminHeaderButton
            type="button"
            disabled={loading || !filtered.length}
            onClick={() =>
              exportLifecycleExcel(filtered, {
                date,
                filterLabel,
                totalDelUnits,
                totalDesignedUnits,
                totalQc2Units,
              })
            }
            title="Export filtered table to Excel"
          >
            <FiDownload size={14} />
            Export Excel
          </AdminHeaderButton>
        </div>
      </AdminPageHeader>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[#8A8A85]">
        <span>
          {loading
            ? "Loading…"
            : `${filtered.length}${
                filtersActive ? ` of ${rows.length}` : ""
              } cases · Del units: `}
          {!loading ? (
            <span className="font-semibold text-[#2C2C2C]">{totalDelUnits}</span>
          ) : null}
          {!loading ? (
            <>
              {" · Designed: "}
              <span
                className="font-semibold text-[#2C2C2C]"
                title="Design units plus model units"
              >
                {totalDesignedUnits}
              </span>
              {" · QC-2: "}
              <span
                className="font-semibold text-[#2C2C2C]"
                title="Design units plus model units on cases that have QC-2"
              >
                {totalQc2Units}
              </span>
            </>
          ) : null}
        </span>
        <span>
          Day window: noon IST → next noon IST
          <span className="mx-2 text-[#DDDDD8]">·</span>
          <span className="font-semibold text-emerald-600">(n)</span> model units
        </span>
      </div>

      <LifecycleTable
        rows={filtered}
        loading={loading}
        error={typeof error === "string" ? error : null}
        emptyMessage="No cases match the selected filters."
        showReference
        onOpenReference={openReference}
        referenceLoadingId={historyLoading ? modalLiveId : null}
        filters={filters}
        onFiltersChange={setFilters}
        staffNames={staffNames}
        uploadNames={uploadNames}
      />

      {modalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close reference modal"
            onClick={closeModal}
          />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-[#E8E8E4] px-5 py-4">
              <div>
                <h2 className="text-base font-semibold text-[#2C2C2C]">
                  Old logs — {modalLiveId}
                </h2>
                <p className="mt-0.5 text-xs text-[#8A8A85]">
                  All live, internal, and external redesign allocations for this
                  case
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-2 text-[#8A8A85] hover:bg-[#F5F5F2] hover:text-[#4A4A48]"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="overflow-auto p-4">
              <LifecycleTable
                rows={historyRows}
                loading={historyLoading}
                error={historyError}
                emptyMessage="No allocation records found for this case."
              />
            </div>
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  );
};

export default CaseLifecycleReport;
